import React, { useEffect, useState, useRef, useImperativeHandle, forwardRef } from 'react';
import {
  LinkAuthenticationElement,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { useSelector } from 'react-redux';
import { selectUser } from 'redux/features/auth/authSlice';
import Button from 'common/components/button';
import { toast } from 'react-toastify';

const CheckoutForm = forwardRef(({ returnUrl, showButton = false, onElementReady, onSetupSuccess, children }, ref) => {
  // STATES
  const [message, setMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useImperativeHandle(ref, () => ({
    submit: () => {
      if (formRef.current) {
        if (typeof formRef.current.requestSubmit === 'function') {
          formRef.current.requestSubmit();
        } else {
          formRef.current.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
        }
      }
    }
  }), []);

  // DATA INTIALIZATION
  const formRef = useRef(null);
  const stripe = useStripe();
  const elements = useElements();
  const userDetails = useSelector(selectUser);  
  const options = {
    layout: {
      type: 'accordion',
      defaultCollapsed: false,
    },
  };

  // HANDLERS
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    const { error, setupIntent } = await stripe.confirmSetup({
      elements,
      confirmParams: {
        return_url: returnUrl, // This is a server-side parameter for the SetupIntent
      },
      redirect: 'if_required', // This is a client-side parameter for Stripe.js SDK behavior
    });

    // Handle result of confirmSetup
    if (error) {
      // This case covers errors like card_error, validation_error, or if redirect: 'never' was used and a redirect was required.
      setMessage(error.message);
    } else if (setupIntent) {
      // SetupIntent object is returned
      if (setupIntent.status === 'succeeded') {
        // Payment method was successfully set up without a redirect
        if (onSetupSuccess) {
          onSetupSuccess(setupIntent.id);
        }
      } else {
        // Handle other unexpected statuses (e.g., canceled, processing, requires_payment_method)
        setMessage(`Setup failed with status: ${setupIntent.status}. Please try again.`);
      }
    } else { // Fallback for unexpected scenarios or if setupIntent is not immediately available
      setMessage('An unexpected error occured.');
    }

    setIsLoading(false);
  };

  useEffect(() => {
    if (message) {
      toast.error(message);
    }
  }, [message]);

  return (
    <div className='w-full h-full'>
      <form
        ref={formRef}
        onSubmit={handleSubmit}
        className='w-full flex flex-col gap-4'
        id='payment-form'
      >
        <LinkAuthenticationElement
          id='link-authentication-element'
          options={{
            defaultValues: {
              email: userDetails?.email,
            },
          }}
        />
        <PaymentElement 
          id='payment-element' 
          options={options}
          onReady={onElementReady}      
        />
        {children}
        {showButton && (
          <Button
            type='submit'
            disabled={isLoading || !stripe || !elements}
            id='submit'
            title='Complete Payment'
            className='mt-4'
          />
        )}
      </form>
    </div>
  );
});

export default CheckoutForm;
